import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { schedulerSignInTarget } from "@/lib/schedulerSignIn";
import { MATERIAL_ACCEPT, MEETING_PLAN, uploadMaterial, validateMaterial } from "@/lib/materials";
import { Card, Button, Icon, Tag } from "@/components/studio/primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const labels = { unpublished: "Saved in Hans — calendar time is not reserved", pending: "Calendar update pending",
  synced: "Calendar time reserved", failed: "Calendar update failed — retry needed", conflict: "The timetables changed in both places" };

export default function AssignedClassCalendar({ cls, enabled, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [preview, setPreview] = useState(null);
  const owned = cls.schedule_owner === "scheduler";
  const openScheduler = async () => {
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    setBusy(true); setError("");
    try {
      const { data } = await orgApi.post(`/org/scheduler/connections/${encodeURIComponent(cls.scheduler_connection_id)}/sign-in`, { destination: "timetables" });
      const target = schedulerSignInTarget(data, cls.scheduler_business_id);
      if (!tab) throw new Error("Allow a new tab to open Scheduler, then try again.");
      tab.location.replace(target);
    } catch (err) { tab?.close(); setError(getOrgErrorMessage(err, "Couldn't open Scheduler. Please try again.")); }
    finally { setBusy(false); }
  };
  const act = async (action) => {
    setBusy(true); setError("");
    try {
      const result = await orgApi.post(`/org/classes/${cls.class_id}/calendar`, {
        action, expected_revision: cls.term_revision || 0, republish: cls.sync_status === "unpublished",
        ...(["migrate", "pull"].includes(action) ? { preview_hash: preview?.preview_hash } : {}),
      });
      if (result.data.deleted) { navigate(`/org/programs/${cls.program_id}`); return; }
      if (result.data.preview) setPreview(result.data.preview); else { setPreview(null); onChanged?.(); }
    } catch (err) { setError(getOrgErrorMessage(err, "Couldn't update the calendar")); }
    finally { setBusy(false); }
  };
  return <Card className="mb-6" data-testid="assigned-calendar-status">
    {owned && <p className="mb-3 text-sm text-[var(--text-secondary)]">Scheduler manages this timetable. Changes made here are saved in Scheduler first. Rosters, attendance and lesson notes stay in Hans.</p>}
    <Tag accent={cls.sync_status === "synced"}>{labels[cls.sync_status] || labels.unpublished}</Tag>
    {!enabled && <p className="mt-3 text-sm text-[var(--text-secondary)]">Your term is saved here. Calendar publishing will be available when Scheduler's timetable support is enabled.</p>}
    {/* The address is the PROGRAM's, and a calendar block genuinely needs one,
        so the gate stays; what it needed was a working link, not a bypass. */}
    {!cls.location_address && <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="publish-gate-location">
      Add the program&rsquo;s location{" "}
      <Link to={`/org/programs/${cls.program_id}?tab=settings`} className="font-medium text-[var(--accent-yellow-text)] underline-offset-2 hover:underline" data-testid="publish-gate-location-link">
        in the program&rsquo;s settings
      </Link>{" "}
      before publishing.
    </p>}
    {error && <p role="alert" className="mt-3 text-[var(--state-danger)]">{error}</p>}
    {cls.sync_error === "availability_conflict" && <p role="alert" className="mt-3 text-sm text-[var(--text-secondary)]">An assigned instructor is already busy during one of these meetings. Move the meeting or resolve the calendar conflict, then retry.</p>}
    <div className="mt-4 flex flex-wrap gap-3">
      {owned && <Button onPress={openScheduler} disabled={busy}>Open timetable in Scheduler</Button>}
      {cls.scheduler_offering_id
        ? <Button onPress={() => act("preview_migration")} disabled={!enabled || busy || !cls.location_address}>Preview timetable migration</Button>
        : <Button onPress={() => act("publish")} disabled={!enabled || busy || !cls.location_address}>
          {owned ? "Refresh timetable" : cls.scheduler_series_id ? cls.sync_status === "unpublished" ? "Publish again" : cls.sync_status === "synced" ? "Update calendar" : "Retry calendar update" : "Publish timetable"}</Button>}
      {!owned && !cls.scheduler_offering_id && <Button variant="ghost" disabled={!enabled || busy || !cls.location_address} onPress={() => act("transfer")}>Use Scheduler for this timetable</Button>}
      {cls.sync_status === "conflict" && <Button variant="ghost" onPress={() => act("preview_pull")} disabled={busy || !enabled}>Review Scheduler dates</Button>}
      <Button variant="ghost" disabled={busy || (!!cls.scheduler_series_id && !enabled)} onPress={() => {
        if (window.confirm(`Delete ${cls.name} and its calendar blocks? Existing students and teaching records will remain.`)) act("delete");
      }}>Delete class</Button>
    </div>
    {preview && <div className="mt-4">
      <p className="text-sm text-[var(--text-secondary)]">{preview.kind === "pull"
        ? preview.deleted ? "This timetable was deleted in Scheduler. The class and its teaching history will remain unpublished in Hans."
          : "Review the Scheduler dates below. Applying them replaces the meeting dates in Hans."
        : "These existing meetings will become calendar blocks. Teaching records and the roster stay attached to this class."}</p>
      <ul className="my-3 max-h-64 overflow-auto">{preview.occurrences.map(o => <li key={o.occurrence_id}>{o.date} · {o.start_time_local} · {o.status}</li>)}</ul>
      <Button onPress={() => act(preview.kind === "pull" ? "pull" : "migrate")} disabled={busy}>{preview.kind === "pull" ? "Apply reviewed Scheduler dates" : "Migrate these meetings"}</Button>
    </div>}
  </Card>;
}

const FIELD = "block w-full rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-2 text-[var(--text-primary)]";
const PREP_NOTE_MAX = 2000;

/**
 * One meeting: when it is, whether it is on, who covers it — and what the
 * organization wants said about it (O7). The prep note rides the occurrence
 * through the same revisioned save as a time change; the lesson plan is a
 * teaching material on the occurrence, one per meeting, replaced by the next.
 * Both reach the instructor and nobody else.
 */
export function AssignedMeetingEditor({ cls, meeting, onClose, onChanged, onSubstitute }) {
  const [day, setDay] = useState(meeting.date), [time, setTime] = useState(meeting.time_local);
  const [duration, setDuration] = useState(meeting.duration_minutes || 60);
  const [note, setNote] = useState(meeting.prep_note || "");
  const [plan, setPlan] = useState(meeting.plan || null);
  const [uploading, setUploading] = useState(null);
  const planInput = useRef(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const save = async (changes) => {
    setBusy(true); setError("");
    try {
      await orgApi.put(`/org/classes/${cls.class_id}/meetings/${meeting.occurrence_id}`, { expected_revision: cls.term_revision || 0, ...changes });
      onClose(); onChanged();
    } catch (err) { setError(getOrgErrorMessage(err, "Couldn't change this meeting")); }
    finally { setBusy(false); }
  };
  const attachPlan = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const problem = validateMaterial(file);
    if (problem) { toast.error(problem); if (planInput.current) planInput.current.value = ""; return; }
    setUploading({ name: file.name, percent: 0 }); setError("");
    try {
      const material = await uploadMaterial(orgApi, `/org/classes/${cls.class_id}`, file, {
        scope: MEETING_PLAN, occurrence_id: meeting.occurrence_id, onProgress: (percent) => setUploading({ name: file.name, percent }),
      });
      setPlan(material); toast.success("Lesson plan attached"); onChanged?.({ keepOpen: true });
    } catch (err) { setError(getOrgErrorMessage(err, "Couldn't attach that file")); }
    finally { setUploading(null); if (planInput.current) planInput.current.value = ""; }
  };
  const removePlan = async () => {
    if (!plan || !window.confirm(`Remove ${plan.filename} from this meeting?`)) return;
    setBusy(true); setError("");
    try {
      await orgApi.delete(`/org/classes/${cls.class_id}/materials/${plan.material_id}`);
      setPlan(null); toast.success("Lesson plan removed"); onChanged?.({ keepOpen: true });
    } catch (err) { setError(getOrgErrorMessage(err, "Couldn't remove the lesson plan")); }
    finally { setBusy(false); }
  };
  const noteChanged = (note.trim() || "") !== (meeting.prep_note || "");
  return <Dialog open onOpenChange={onClose}><DialogContent className="studio border-[var(--studio-divider)] bg-[var(--studio-overlay)] text-[var(--text-primary)]">
    <DialogHeader><DialogTitle>{cls.name}</DialogTitle><DialogDescription>{meeting.status === "cancelled" ? "Cancelled meeting" : "Scheduled meeting"}</DialogDescription></DialogHeader>
    <div className="grid grid-cols-3 gap-3">
      <label className="text-sm text-[var(--text-secondary)]">Date<input className={FIELD} type="date" value={day} onChange={e => setDay(e.target.value)} /></label>
      <label className="text-sm text-[var(--text-secondary)]">Start time<input className={FIELD} type="time" value={time} onChange={e => setTime(e.target.value)} /></label>
      <label className="text-sm text-[var(--text-secondary)]">Minutes<input className={FIELD} type="number" min="1" max="1440" value={duration} onChange={e => setDuration(Number(e.target.value))} /></label>
    </div>
    {/* The org's brief for whoever teaches this session. Saved with the
        meeting, shown to the instructor beside it, never to a family. */}
    <label className="text-sm text-[var(--text-secondary)]">Note to the instructor
      <textarea className={`${FIELD} min-h-[72px]`} value={note} maxLength={PREP_NOTE_MAX} placeholder="Bring the metronome — the piano is out of tune."
        onChange={e => setNote(e.target.value)} data-testid="meeting-prep-note" />
    </label>
    <div className="rounded-lg border border-[var(--studio-divider)] p-3" data-testid="meeting-plan">
      <p className="text-sm font-medium text-[var(--text-secondary)]">Lesson plan</p>
      {uploading ? <p className="mt-1 text-sm text-[var(--text-tertiary)]" data-testid="meeting-plan-uploading">Uploading {uploading.name}… {uploading.percent}%</p>
        : plan ? <p className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <a href={plan.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium text-[var(--accent-yellow-text)] underline-offset-2 hover:underline" data-testid="meeting-plan-link">
            <Icon name="paperclip" size={13} /> {plan.filename}
          </a>
        </p>
        : <p className="mt-1 text-sm text-[var(--text-tertiary)]" data-testid="meeting-plan-empty">Nothing attached. One file per meeting; a new one replaces it.</p>}
      <input ref={planInput} type="file" accept={MATERIAL_ACCEPT} className="hidden" aria-label="Attach a lesson plan" onChange={attachPlan} data-testid="meeting-plan-input" />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" disabled={busy || !!uploading} onPress={() => planInput.current?.click()} data-testid="meeting-plan-attach">{plan ? "Replace plan" : "Attach lesson plan"}</Button>
        {plan && <Button variant="ghost" size="sm" disabled={busy || !!uploading} onPress={removePlan} data-testid="meeting-plan-remove">Remove</Button>}
      </div>
    </div>
    {error && <p role="alert" className="text-[var(--state-danger)]">{error}</p>}
    <div className="flex flex-wrap gap-3">
      <Button disabled={busy || !day || !time || duration < 1 || duration > 1440} onPress={() => save({ date: day, start_time_local: time, duration_minutes: duration, ...(noteChanged ? { prep_note: note.trim() } : {}) })}>Save changes</Button>
      <Button variant="ghost" disabled={busy} onPress={() => save({ status: meeting.status === "cancelled" ? "scheduled" : "cancelled" })}>
        {meeting.status === "cancelled" ? "Restore meeting" : "Cancel meeting"}</Button>
      {meeting.status !== "cancelled" && <Button variant="ghost" onPress={onSubstitute}>Assign substitute</Button>}
    </div>
  </DialogContent></Dialog>;
}
